import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X } from "lucide-react";
import { API, authHeaders, formatApiError } from "./AdminPage";
import { useAdminT } from "./adminI18n";

const EMPTY = { text: "", name: "", role: "", text_en: "", name_en: "", role_en: "" };

const inputCls =
  "w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold";

export const TestimonialsTab = () => {
  const { t } = useAdminT();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios.get(`${API}/testimonials`).then((res) => setItems(res.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const startEdit = (t) => {
    setForm({ text: t.text, name: t.name, role: t.role, text_en: t.text_en || "", name_en: t.name_en || "", role_en: t.role_en || "" });
    setEditingId(t.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/testimonials/${editingId}`, form, { headers: authHeaders() });
        toast.success(t("تم تحديث الرأي"));
      } else {
        await axios.post(`${API}/admin/testimonials`, form, { headers: authHeaders() });
        toast.success(t("تمت الإضافة"));
      }
      setForm(EMPTY);
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("حذف هذا الرأي نهائياً؟"))) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { headers: authHeaders() });
      toast.success(t("تم الحذف"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-testimonials-tab">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif text-3xl text-luxury-bone">{t("آراء العملاء")} ({items.length})</h2>
        <button
          data-testid="testimonial-add-button"
          onClick={() => { setForm(EMPTY); setEditingId(null); setShowForm(!showForm); }}
          className="flex items-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-luxury-goldLight transition-colors"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          {t("إضافة رأي جديد")}
        </button>
      </div>

      {showForm && (
        <form data-testid="testimonial-form" onSubmit={save} className="bg-luxury-surface border border-luxury-border p-8 mb-10 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-2xl text-luxury-gold">{editingId ? t("تعديل الرأي") : t("رأي جديد")}</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-luxury-gray hover:text-luxury-bone" aria-label={t("إغلاق")}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <div>
            <label className="block text-sm text-luxury-gray mb-2">{t("نص الرأي *")}</label>
            <textarea data-testid="testimonial-text-input" dir="auto" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={4} className={inputCls} required />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm text-luxury-gray mb-2">{t("الاسم")}</label>
              <input data-testid="testimonial-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder={t("مثال: مشاركة في الورشة")} />
            </div>
            <div>
              <label className="block text-sm text-luxury-gray mb-2">{t("الوصف / المصدر")}</label>
              <input data-testid="testimonial-role-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} placeholder={t("مثال: ورشة مبارح")} />
            </div>
          </div>
          <div className="border-t border-luxury-border pt-5">
            <p className="text-luxury-gold text-sm mb-4">{t("النسخة الإنجليزية (اختياري) — English version")}</p>
            <div className="space-y-5">
              <div>
                <label className="block text-sm text-luxury-gray mb-2">Testimonial text (EN)</label>
                <textarea dir="ltr" value={form.text_en} onChange={(e) => setForm({ ...form, text_en: e.target.value })} rows={3} className={inputCls} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-luxury-gray mb-2">Name (EN)</label>
                  <input dir="ltr" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className={inputCls} placeholder="e.g. Workshop attendee" />
                </div>
                <div>
                  <label className="block text-sm text-luxury-gray mb-2">Source (EN)</label>
                  <input dir="ltr" value={form.role_en} onChange={(e) => setForm({ ...form, role_en: e.target.value })} className={inputCls} placeholder="e.g. Live workshop" />
                </div>
              </div>
            </div>
          </div>

          <button data-testid="testimonial-save-button" type="submit" disabled={saving} className="bg-luxury-gold text-luxury-black font-semibold px-8 py-3 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60 flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editingId ? t("حفظ التعديلات") : t("إضافة الرأي")}
          </button>
        </form>
      )}

      <div className="space-y-4">
        {items.map((item, i) => (
          <div key={item.id} data-testid={`admin-testimonial-row-${i}`} className="bg-luxury-surface border border-luxury-border p-5">
            <p className="text-luxury-bone leading-relaxed mb-3" dir="auto">{item.text}</p>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <p className="text-luxury-gray text-sm">{item.name} — {item.role}</p>
              <div className="flex items-center gap-2">
                <button data-testid={`admin-testimonial-edit-${i}`} onClick={() => startEdit(item)} className="flex items-center gap-2 border border-luxury-border text-luxury-gold px-4 py-2 rounded-full text-sm hover:border-luxury-gold transition-colors">
                  <Pencil className="w-4 h-4" strokeWidth={1.5} /> {t("تعديل")}
                </button>
                <button data-testid={`admin-testimonial-delete-${i}`} onClick={() => remove(item.id)} className="flex items-center gap-2 border border-luxury-border text-red-400 px-4 py-2 rounded-full text-sm hover:border-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} /> {t("حذف")}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
